/**
 * 置く場所の選択（召喚・発動の位置）を動画の上で選ぶ（Issue #467）。応答タブのあいだ動画は止まっているので、
 * 今のフレームのうち置けるゾーンの行（横は動画の幅いっぱい）を canvas に切り抜いて描き、その上にゾーンのボタンを重ねる。
 * 置けないゾーンは薄い枠だけで押せない。count か所そろったら onDone(places)。
 * 動画・ゾーンが無ければ（jsdom・動画の無い試合）MiniField（ゾーンの図）に落とす。
 * ライブ映像（liveVideo.js）は `drawSource`（canvas）から切り抜く。
 */
import { useEffect, useRef, useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { pickPlace, placeKey } from './inputView';
import MiniField from './MiniField';
import { zoneKeyOfPlaceKey } from './stage';
import { placeCrop, zonesInCrop } from './placeCrop';

export { placeCrop } from './placeCrop';

export default function PlaceOnVideo({ options = [], count = 1, videoEl = null, videoSize = null, zones = null, board = null, thumbs = {}, busy = false, onDone, onPicking }) {
  const colors = usePlayerColors();
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const [box, setBox] = useState(null);
  const [picked, setPicked] = useState([]);
  const crop = placeCrop(options, zones, videoSize);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [!!videoEl, !!crop]);   // eslint-disable-line react-hooks/exhaustive-deps
  const scale = crop && box?.w > 0 ? Math.min(box.w / crop.w, box.h > 0 ? box.h / crop.h : Infinity) : 0;
  const cw = crop ? crop.w * scale : 0; const ch = crop ? crop.h * scale : 0;
  // 今のフレームを描く（止めた動画でも、シークし直したら描き直す）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !videoEl || !crop || !(cw > 0)) return undefined;
    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(cw * dpr); canvas.height = Math.round(ch * dpr);
      const ctx = canvas.getContext?.('2d');
      if (!ctx) return;
      try { ctx.drawImage(videoEl.drawSource ?? videoEl, crop.x, crop.y, crop.w, crop.h, 0, 0, canvas.width, canvas.height); } catch (e) { /* まだフレームが無い */ }
    };
    draw();
    videoEl.addEventListener?.('seeked', draw);
    videoEl.addEventListener?.('loadeddata', draw);
    return () => { videoEl.removeEventListener?.('seeked', draw); videoEl.removeEventListener?.('loadeddata', draw); };
  }, [videoEl, crop?.x, crop?.y, crop?.w, crop?.h, cw, ch]);   // eslint-disable-line react-hooks/exhaustive-deps

  const allowed = new Map(options.map((p) => [zoneKeyOfPlaceKey(placeKey(p)), p]));
  const tap = (zoneKey) => {
    const place = allowed.get(zoneKey);
    if (!place || busy) return;
    const r = pickPlace(picked, place, count);
    if (r.done) { setPicked([]); onDone?.(r.places); return; }
    setPicked(r.places);
    onPicking?.(r.places);
  };

  if (!videoEl || !crop) {
    return <MiniField options={options} count={count} board={board} thumbs={thumbs} busy={busy} onDone={onDone} onPicking={onPicking} />;
  }
  const pickedKeys = new Set(picked.map((p) => zoneKeyOfPlaceKey(placeKey(p))));
  const inCrop = zonesInCrop(zones, crop);
  return (
    <div ref={hostRef} data-testid="place-on-video" style={{ height: '100%', minHeight: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      {count > 1 ? <div style={{ fontSize: 12, opacity: 0.8 }}>{count} か所選ぶ（{picked.length} / {count}）</div> : null}
      <div style={{ position: 'relative', width: cw, height: ch, flexShrink: 0 }}>
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: cw, height: ch, borderRadius: 6, background: '#0b1020' }} />
        {inCrop.map(([key, r]) => {
          const ok = allowed.has(key);
          const on = pickedKeys.has(key);
          const owner = Number(key.split(':')[1]);
          const label = key.startsWith('field:') ? 'F' : key.split(':')[2];
          return (
            <button key={key} type="button" data-testid="place-zone" data-zone={key} disabled={!ok || busy} aria-pressed={on} onClick={() => tap(key)}
              aria-label={`${owner === 0 ? 'P1' : 'P2'} ${key.startsWith('monster') ? 'モンスター' : key.startsWith('field') ? 'フィールド' : '魔法罠'} ${label}`}
              style={{ position: 'absolute', left: (r.x - crop.x) * scale, top: (r.y - crop.y) * scale, width: r.w * scale, height: r.h * scale, padding: 0, borderRadius: 4, cursor: ok ? 'pointer' : 'default',
                border: `${ok ? 3 : 1}px solid ${on ? '#ffd36b' : ok ? '#4fc3ff' : (playerColor(owner, colors) ?? colors[0])}`, opacity: ok ? 1 : 0.45,
                background: on ? 'rgba(255,211,107,0.35)' : ok ? 'rgba(79,195,255,0.18)' : 'transparent', color: '#fff', fontWeight: 900, fontSize: 14, textShadow: '0 0 4px #000' }}>
              {ok ? label : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
